<template>
  <div class="field-group">
    <label
      class="field-label"
      :class="{ required }"
    >
      {{ label }}
      <span
        v-if="helpText"
        class="help-icon"
        :title="helpText"
        >?</span
      >
    </label>

    <div class="keywords-input">
      <input
        v-model="palavra"
        type="text"
        class="field-input keyword-field"
        placeholder="Digite uma palavra-chave e pressione Enter"
        @keydown.enter.prevent="adicionarPalavra"
        maxlength="50"
      />
      <button
        type="button"
        class="btn-adicionar"
        @click.prevent="adicionarPalavra"
      >
        Adicionar
      </button>
    </div>

    <div
      v-if="palavrasChave && palavrasChave.length > 0"
      class="keywords-tags"
    >
      <h6>Palavras selecionadas:</h6>
      <span
        v-for="(item, index) in palavrasChave"
        :key="index"
        class="keyword-tag"
      >
        {{ item }}
        <button
          type="button"
          class="tag-remove"
          title="Remover palavra-chave"
          @click.prevent="removerPalavra(index)"
        >
          ×
        </button>
      </span>
    </div>

    <span
      v-if="errorMessage"
      class="error-text"
    >
      {{ errorMessage }}
    </span>

    <!-- Modal de Aviso -->
    <Aviso
      v-if="modalAviso.visivel"
      :texto="modalAviso.texto"
      :sucesso="false"
      @fecharModal="fecharModal"
    />
  </div>
</template>

<script setup>
import { ref, reactive } from "vue";
import Aviso from "@/components/common/aviso.vue";

const props = defineProps({
  label: {
    type: String,
    default: "Palavras-chave",
  },
  required: {
    type: Boolean,
    default: true,
  },
  helpText: {
    type: String,
    default: "Adicione palavras-chave",
  },
  errorMessage: {
    type: String,
    default: "",
  },
});

const palavra = ref("");
const palavrasChave = defineModel({ default: () => [] });

const modalAviso = reactive({
  visivel: false,
  texto: "",
});

function exibirErro(mensagem) {
  modalAviso.texto = mensagem;
  modalAviso.visivel = true;
}

function fecharModal() {
  modalAviso.visivel = false;
}

function adicionarPalavra() {
  const termo = palavra.value.trim();

  if (!Array.isArray(palavrasChave.value)) {
    palavrasChave.value = [];
  }

  if (termo === "") {
    exibirErro("A palavra-chave não pode ser vazia");
    return;
  }

  const termosSeparados = termo
    .split(/[;,]+/)
    .map((p) => p.trim())
    .filter((p) => p !== "");

  if (termosSeparados.length === 0) {
    exibirErro("A palavra-chave não pode ser vazia");
    return;
  }

  const repetidas = termosSeparados.filter((p) =>
    palavrasChave.value.some(
      (existente) => existente.toLowerCase() === p.toLowerCase()
    )
  );

  const novasPalavras = termosSeparados.filter(
    (p) =>
      !palavrasChave.value.some(
        (existente) => existente.toLowerCase() === p.toLowerCase()
      )
  );

  if (novasPalavras.length > 0) {
    palavrasChave.value.push(...novasPalavras);
    palavra.value = "";
    if (repetidas.length > 0) {
      exibirErro(
        repetidas.length === 1
          ? `A palavra-chave "${repetidas[0]}" já foi adicionada.`
          : `As seguintes palavras-chave já foram adicionadas: ${repetidas.join(", ")}.`
      );
    }
  } else {
    exibirErro(
      repetidas.length === 1
        ? `Não é permitido palavras repetidas: "${repetidas[0]}" já foi adicionada.`
        : "Não é permitido palavras repetidas."
    );
  }
}

function removerPalavra(index) {
  if (Array.isArray(palavrasChave.value)) {
    palavrasChave.value.splice(index, 1);
  }
}
</script>

<style scoped>
.field-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-label {
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--preto);
  display: flex;
  align-items: center;
  gap: 6px;
}

.field-label.required::before {
  content: "*";
  color: var(--roxo-medio);
}

.help-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--cinza-medio);
  color: var(--branco);
  font-size: 0.65rem;
  font-weight: bold;
  cursor: pointer;
}

.field-input {
  border: 1px solid var(--roxo-claro);
  border-radius: 4px;
  padding: 8px 10px;
  font-size: 0.9rem;
  color: var(--preto);
  outline: none;
  transition: border-color 0.2s;
  width: 100%;
  box-sizing: border-box;
}

.field-input:focus {
  border-color: var(--roxo-medio);
  box-shadow: 0 0 0 2px hsla(258, 46%, 46%, 0.12);
}

/* Keywords */
.keywords-input {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
}

.keyword-field {
  flex: 1;
}

/* .btn-adicionar removido: idêntico ao definido em _utilities.css */

.keywords-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
  border: 1px solid var(--roxo-primario);
  border-radius: 15px;
  padding: 20px 5px 10px 10px;
}

.keywords-tags h6 {
  position: absolute;
  margin-top: -30px;
  margin-left: 5px;
  z-index: 100;
  background-color: var(--branco);
  color: var(--roxo-primario);
  font-size: 15px;
}

.keyword-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--roxo-superficie);
  color: var(--roxo-medio);
  border-radius: 12px;
  padding: 3px 10px;
  font-size: 0.8rem;
  font-weight: 500;
}

.tag-remove {
  background: none;
  border: none;
  color: var(--roxo-medio);
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s;
}

.tag-remove:hover {
  color: var(--roxo-primario);
  font-weight: bold;
}

.error-text {
  font-size: 0.8rem;
  color: var(--vermelho);
  font-weight: 500;
  margin-top: 2px;
}
</style>
